import React, { useState, useMemo, useRef, useEffect } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './AppointmentTable.module.css';
import CalendarIcon from '../../../../images/calendar-icon.svg';
import { useDispatch } from 'react-redux';
import ViewIcon from '../../../svgIcons/ViewIcon';
import DownloadIcons from '../../../svgIcons/DownloadIcons';
import TrashIcon from '../../../svgIcons/TrashIcon';
import SearchInput from '../../../common/search-input/SearchInput';
// import TabGroup from '../../../common/searchTabGroup/TabGroup';
import MaintenanceIcon from '../../../svgIcons/MaintennaceIcon';
import Pagination from '../../../common/pagination/pagination';
import { Vendor, visits } from '../types';
import Loader from '../../../common/loader/Loader';
import TabGroup from '../../../tabGroup/TabGroup';
import { datePresetOptions, getLockerColor } from '../../../../constants/constants';
import { AppDispatch } from '../../../../redux/rootReducer';
import { downloadGuestCard } from '../../../../redux/guest-card/actions/guestCard.action';
import { parsePhoneNumber } from 'awesome-phonenumber';

/** Same as AppointmentPopup: date is stored relative to UTC; local .format() shifts the calendar day in US timezones. */
function formatAppointmentDateDisplay(appointmentDate: Date | string | undefined | null): string {
    if (appointmentDate == null || appointmentDate === '') return '';
    return moment(appointmentDate).utc().format('MM-DD-YYYY');
}

function appointmentDateToYyyyMmDd(appointmentDate: Date | string): string {
    return moment(appointmentDate).utc().format('YYYY-MM-DD');
}

/**
 * Vendor range boundaries from API: often YYYY-MM-DD, sometimes ISO (e.g. ...T00:00:00.000Z).
 * Parsing with moment(iso) alone shifts the calendar day in local time and can drop the last day
 * from the expanded rows. Align with generateVendorEvents (date-only) + UTC calendar for ISO.
 */
function parseVendorCalendarDay(v: string): moment.Moment {
    const asDay = moment(v, 'YYYY-MM-DD', true);
    if (asDay.isValid()) {
        return asDay.startOf('day');
    }
    const utc = moment.utc(v);
    if (utc.isValid()) {
        return moment(utc.format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day');
    }
    return moment(v).startOf('day');
}

/** Ascending sort: calendar day + start time, then stable tie-breakers. */
function getRowSortTimestamp(row: {
    type: 'appointment' | 'vendor';
    data: visits | Vendor;
    date?: moment.Moment;
}): number {
    if (!row.date) return 0;

    const dayStart = row.date.clone().startOf('day');

    if (row.type === 'appointment') {
        const v = row.data as visits;
        if (!v.appointmentStartTime) return dayStart.valueOf();
        const t =
            typeof v.appointmentStartTime === 'string'
                ? moment(v.appointmentStartTime, ['h:mm A', 'HH:mm', moment.ISO_8601], true)
                : moment(v.appointmentStartTime);
        if (!t.isValid()) return dayStart.valueOf();
        return dayStart.clone().hour(t.hour()).minute(t.minute()).second(t.second() || 0).valueOf();
    }

    const vd = row.data as Vendor;
    if (!vd.startTime) return dayStart.valueOf();
    const t = moment(vd.startTime, 'HH:mm A', true);
    if (!t.isValid()) return dayStart.valueOf();
    return dayStart.clone().hour(t.hour()).minute(t.minute()).second(0).valueOf();
}

const SEARCH_DEBOUNCE_MS = 2000;

interface AppointmentTableProps {
    appointment: visits[] | null;
    vendor: Vendor[] | null;
    handleRowClick: (item: visits | Vendor, isVendor: boolean) => void;
    handleDeleteClick: (item: visits | Vendor) => void;
    apiStartDate?: string | null;
    apiEndDate?: string | null;
    onApiDateRangeChange?: (startDate: string, endDate: string) => void;
}
const AppointmentTable: React.FC<AppointmentTableProps> = ({
    appointment,
    vendor,
    handleRowClick,
    handleDeleteClick,
    apiStartDate,
    apiEndDate,
    onApiDateRangeChange,
}) => {
    const dispatch: AppDispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [startDate, endDate] = dateRange;
    const [datePreset, setDatePreset] = useState<string>('This week');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const today = new Date();
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const handleDownload = (visit: visits) => {
        setIsLoading(true);
        dispatch(downloadGuestCard(visit?.appointmentId, setIsLoading));
    };

    const autoSelectEndDate = () => {
        if (startDate && !endDate) {
            setDateRange([startDate, startDate > today ? startDate : today]);
        }
    };

    const formatPhoneNumber = (phone: string) => {
        if (!phone || phone === "-") return "-";

        try {
            const phoneNumber = parsePhoneNumber(`+${phone}`);
            if (phoneNumber && phoneNumber.valid) {
                return phoneNumber.number.international;
            }
            return phone; // Return original if parsing fails
        } catch (error) {
            console.warn('Phone number parsing failed:', error);
            return phone; // Return original if parsing fails
        }
    };

    const getRangeForPreset = (preset: string): [Date, Date] => {
        const now = moment();

        switch (preset) {
            case 'This week': {
                const start = now.clone().startOf('week');
                const end = now.clone().endOf('week');
                return [start.toDate(), end.toDate()];
            }
            case 'Last week': {
                const start = now.clone().subtract(1, 'week').startOf('week');
                const end = now.clone().subtract(1, 'week').endOf('week');
                return [start.toDate(), end.toDate()];
            }
            case 'This month': {
                const start = now.clone().startOf('month');
                const end = now.clone().endOf('month');
                return [start.toDate(), end.toDate()];
            }
            case 'Last month': {
                const start = now.clone().subtract(1, 'month').startOf('month');
                const end = now.clone().subtract(1, 'month').endOf('month');
                return [start.toDate(), end.toDate()];
            }
            default: {
                const start = now.clone().startOf('month');
                const end = now.clone().endOf('month');
                return [start.toDate(), end.toDate()];
            }
        }
    };

    const getPresetForRange = (start: Date, end: Date): string => {
        const startM = moment(start).startOf('day');
        const endM = moment(end).startOf('day');

        const isSameRange = (preset: string) => {
            const [pStart, pEnd] = getRangeForPreset(preset);
            return (
                startM.isSame(moment(pStart).startOf('day')) &&
                endM.isSame(moment(pEnd).startOf('day'))
            );
        };

        if (isSameRange('This week')) return 'This week';
        if (isSameRange('Last week')) return 'Last week';
        if (isSameRange('This month')) return 'This month';
        if (isSameRange('Last month')) return 'Last month';
        return 'Custom';
    };

    // Sync DatePicker with parent (when toggling calendar <-> list).
    // If parent provides dates, respect them and infer matching preset (or Custom).
    useEffect(() => {
        if (!apiStartDate || !apiEndDate) return;

        const nextStart = moment(apiStartDate, 'YYYY-MM-DD', true);
        const nextEnd = moment(apiEndDate, 'YYYY-MM-DD', true);
        if (!nextStart.isValid() || !nextEnd.isValid()) return;

        const currStart = startDate ? moment(startDate) : null;
        const currEnd = endDate ? moment(endDate) : null;
        if (currStart?.isSame(nextStart, 'day') && currEnd?.isSame(nextEnd, 'day')) return;

        setDateRange([nextStart.toDate(), nextEnd.toDate()]);
        setDatePreset(getPresetForRange(nextStart.toDate(), nextEnd.toDate()));
    }, [apiStartDate, apiEndDate]);

    // Initialize with "This week" on first load when parent has no range yet (default list view).
    const hasInitializedRef = useRef(false);
    useEffect(() => {
        if (hasInitializedRef.current) return;
        if (apiStartDate && apiEndDate) {
            hasInitializedRef.current = true;
            return; // Parent controls dates
        }
        if (startDate && endDate) {
            hasInitializedRef.current = true;
            return; // Already initialized
        }

        const [presetStart, presetEnd] = getRangeForPreset('This week');
        setDateRange([presetStart, presetEnd]);
        hasInitializedRef.current = true;
    }, [apiStartDate, apiEndDate, startDate, endDate]);

    // Combine appointments and vendors into a single array for pagination
    // const allRows = useMemo(() => {
    //     const rows: Array<{ type: 'appointment' | 'vendor'; data: visits | Vendor; date?: moment.Moment }> = [];

    //     // Add appointments
    //     if (appointment && appointment.length > 0) {
    //         appointment.forEach((visit: visits) => {
    //             rows.push({ type: 'appointment', data: visit, date: moment(visit.appointmentDate) });
    //         });
    //     }
    //     appointment
    //     // Add vendors (expanded by date)
    //     if (vendor && vendor.length > 0) {
    //         vendor.forEach((vendorinfo: Vendor) => {
    //             const vendorStartDate = moment(vendorinfo.startDate);
    //             const vendorEndDate = moment(vendorinfo.endDate);
    //             const dates = [];
    //             let currentDate = vendorStartDate.clone();
    //             while (currentDate.isSameOrBefore(vendorEndDate)) {
    //                 dates.push(currentDate.clone());
    //                 currentDate.add(1, 'day');
    //             }
    //             dates.forEach((date) => {
    //                 rows.push({ type: 'vendor', data: vendorinfo, date });
    //             });
    //         });
    //     }

    //     return rows;
    // }, [appointment, vendor]);

    const allRows = useMemo(() => {
        const rows: Array<{
            type: 'appointment' | 'vendor';
            data: visits | Vendor;
            date?: moment.Moment;
        }> = [];

        // Add appointments
        if (appointment && appointment.length > 0) {
            appointment.forEach((visit: visits) => {
                rows.push({
                    type: 'appointment',
                    data: visit,
                    date: moment.utc(visit.appointmentDate),
                });
            });
        }

        // Add vendors (expanded by date)
        if (vendor && vendor.length > 0) {
            vendor.forEach((vendorinfo: Vendor) => {
                const vendorStartDate = parseVendorCalendarDay(vendorinfo.startDate);
                const vendorEndDate = parseVendorCalendarDay(vendorinfo.endDate);

                let currentDate = vendorStartDate.clone();

                while (currentDate.isSameOrBefore(vendorEndDate, 'day')) {
                    rows.push({
                        type: 'vendor',
                        data: vendorinfo,
                        date: currentDate.clone(),
                    });

                    currentDate.add(1, 'day');
                }
            });
        }

        rows.sort((a, b) => {
            const ta = getRowSortTimestamp(a);
            const tb = getRowSortTimestamp(b);
            if (ta !== tb) return ta - tb;
            if (a.type !== b.type) return a.type === 'appointment' ? -1 : 1;
            if (a.type === 'appointment') {
                const idA = String((a.data as visits).appointmentId ?? '');
                const idB = String((b.data as visits).appointmentId ?? '');
                return idA.localeCompare(idB);
            }
            const idA = String((a.data as Vendor).id ?? '');
            const idB = String((b.data as Vendor).id ?? '');
            if (idA !== idB) return idA.localeCompare(idB);
            return (a.date?.format('YYYY-MM-DD') ?? '').localeCompare(b.date?.format('YYYY-MM-DD') ?? '');
        });

        return rows;

    }, [appointment, vendor]);

    // When a date range is selected in table, notify parent to trigger API call (same as calendar)

    const lastSentRangeRef = useRef<string | null>(null);
    useEffect(() => {
        if (!onApiDateRangeChange) return;
        if (!startDate || !endDate) return;

        const start = moment(startDate).format('YYYY-MM-DD');
        const end = moment(endDate).format('YYYY-MM-DD');
        const key = `${start}_${end}`;
        if (lastSentRangeRef.current === key) return;

        lastSentRangeRef.current = key;
        onApiDateRangeChange(start, end);
    }, [startDate, endDate, onApiDateRangeChange]);

    useEffect(() => {
        const id = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(id);
    }, [searchTerm]);

    const isSearchDebouncing = searchTerm !== debouncedSearchTerm;

    const filteredRows = useMemo(() => {
        const q = debouncedSearchTerm.trim().toLowerCase();

        const matchesSearch = (row: { type: 'appointment' | 'vendor'; data: visits | Vendor; date?: moment.Moment }) => {
            if (!q) return true;
            if (row.type === 'appointment') {
                const v = row.data as visits;
                const visitorName = v.firstName && v.lastName ? `${v.firstName} ${v.lastName}` : v.userName || '';
                const unit = v.unitYardiId || v.unitName || '';
                const email = v.email || '';
                const phone = v.phoneNumber ? `+${v.countryCode ?? ''}${v.phoneNumber}` : '';
                const locker = v.locker_number != null ? String(v.locker_number) : '';
                const dateStr = v.appointmentDate ? formatAppointmentDateDisplay(v.appointmentDate) : '';
                return [visitorName, unit, email, phone, locker, dateStr].some((x) =>
                    String(x).toLowerCase().includes(q),
                );
            }

            const vd = row.data as Vendor;
            const vendorName = vd.vendorName || '';
            const vendorEmail = vd.vendorEmail || '';
            const vendorPhone = vd.vendorPhone ? `+${vd.vendorPhone}` : '';
            const locker = vd.lockerNumber != null ? String(vd.lockerNumber) : '';
            const dateStr = row.date ? row.date.format('MM-DD-YYYY') : '';
            return [vendorName, vendorEmail, vendorPhone, locker, dateStr].some((x) =>
                String(x).toLowerCase().includes(q),
            );
        };

        const matchesDateRange = (row: {
            type: 'appointment' | 'vendor';
            data: visits | Vendor;
            date?: moment.Moment;
        }) => {
            if (!startDate || !endDate) return true;
            const startStr = moment(startDate).format('YYYY-MM-DD');
            const endStr = moment(endDate).format('YYYY-MM-DD');
            if (row.type === 'appointment') {
                const v = row.data as visits;
                if (!v.appointmentDate) return false;
                const dayStr = appointmentDateToYyyyMmDd(v.appointmentDate);
                return dayStr >= startStr && dayStr <= endStr;
            }
            if (!row.date) return false;
            const filterStart = moment(startDate).startOf('day');
            const filterEnd = moment(endDate).startOf('day');
            return row.date.isSameOrAfter(filterStart, 'day') && row.date.isSameOrBefore(filterEnd, 'day');
        };

        return allRows.filter((row) => matchesSearch(row) && matchesDateRange(row));
    }, [allRows, startDate, endDate, debouncedSearchTerm]);

    // Paginate filtered rows
    const paginatedRows = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return filteredRows.slice(startIndex, endIndex);
    }, [filteredRows, currentPage, pageSize]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate, debouncedSearchTerm]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Scroll table to top when page changes
    useEffect(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
    }, [currentPage]);

    return (
        <div className={styles.tableWrapper}>
            {(isLoading || isSearchDebouncing) && <Loader />}
            <div className={styles.tableTopBar}>
                <div className={styles.tableTopBarSearch}>
                    <SearchInput
                        value={searchTerm}
                        placeholder="Search Visitor/Vendor Name"
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: 360, maxWidth: '100%' }}
                    />
                </div>
                <div className={styles.tableTopBarFilters}>
                    <div className={styles.filterTabs}>
                        <TabGroup
                            options={datePresetOptions}
                            value={datePreset}
                            onChange={(val) => {
                                const preset = String(val);

                                // Prevent unnecessary updates if same preset is clicked
                                if (preset === datePreset) return;

                                setDatePreset(preset);

                                // For presets, immediately set the corresponding date range
                                // so the DatePicker reflects the selected filter.
                                if (preset !== 'Custom') {
                                    const [presetStart, presetEnd] = getRangeForPreset(preset);
                                    setDateRange([presetStart, presetEnd]);
                                }
                            }}
                        />
                    </div>
                    <div
                        className={`${styles.datePickerWrapper} datePicker-wrapper${datePreset !== 'Custom' ? ` ${styles.disabledDatePicker}` : ''}`}
                    >
                        <DatePicker
                            selected={startDate}
                            popperPlacement="bottom-start"
                            dateFormat="MM/dd/yyyy"
                            showPopperArrow={false}
                            placeholderText="Start Date - End Date"
                            name="Start Date"
                            className="custom-dateInput"
                            disabled={datePreset !== 'Custom'}
                            isClearable={false}
                            selectsRange={true}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(e) => {
                                setDateRange(e);
                            }}
                            onCalendarClose={autoSelectEndDate}
                        />
                        <img src={CalendarIcon} alt="calendar" />
                    </div>

                </div>
            </div>
            <div ref={tableContainerRef} className={styles.tableContainer}>
                <table className={styles.appointmentsTable}>
                    <thead>
                        <tr>
                            <th>Unit</th>
                            <th>Visitor Name</th>
                            <th>Locker Number</th>
                            <th>Time</th>
                            <th>Date</th>
                            <th>Email</th>
                            <th>Phone Number</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRows.length > 0 ? (
                            paginatedRows.map((row, index: number) => {
                                if (row.type === 'appointment') {
                                    const visit = row.data as visits;
                                    const isDeleted = visit?.hasAdminDeleted === 1 || visit?.deletedAt !== null;
                                    const visitorName = visit.firstName && visit.lastName
                                        ? `${visit.firstName} ${visit.lastName}`
                                        : visit.userName || '-';
                                    const phoneNumber = visit.phoneNumber && visit.countryCode
                                        ? `+${visit.countryCode}${visit.phoneNumber}`
                                        : visit.phoneNumber || '-';
                                    const timeRange = visit.appointmentStartTime && visit.appointmentEndTime
                                        ? (() => {
                                            const startTime = typeof visit.appointmentStartTime === 'string'
                                                ? moment(visit.appointmentStartTime, 'h:mm A').format('h:mm A')
                                                : moment(visit.appointmentStartTime).format('h:mm A');
                                            const endTimeStr = typeof visit.appointmentEndTime === 'string'
                                                ? visit.appointmentEndTime.trim()
                                                : moment(visit.appointmentEndTime).format('HH:mm');
                                            const endTime = endTimeStr === '23:59'
                                                ? moment(endTimeStr, 'HH:mm').add(1, 'minute').format('h:mm A')
                                                : typeof visit.appointmentEndTime === 'string'
                                                    ? moment(visit.appointmentEndTime, 'h:mm A').format('h:mm A')
                                                    : moment(visit.appointmentEndTime).format('h:mm A');
                                            return `${startTime} - ${endTime}`;
                                        })()
                                        : '-';
                                    // Use unique key based on appointment ID and date
                                    const uniqueKey = visit?.id ? `appointment-${visit.id}-${visit.appointmentDate}` : `appointment-${index}-${visit.appointmentDate}`;
                                    return (
                                        <tr
                                            key={uniqueKey}
                                            className={`${styles.tableRow} ${isDeleted ? styles.deletedRow : ''}`}
                                            onClick={() => handleRowClick(visit, false)}
                                        >
                                            <td>{visit?.unitYardiId || visit.unitName || '-'}</td>
                                            <td>{visitorName}</td>
                                            <td>
                                                <span
                                                    className={styles.lockerBadge}
                                                    style={{
                                                        backgroundColor: getLockerColor(visit.locker_number),
                                                    }}
                                                >
                                                    {visit.locker_number}
                                                </span>
                                            </td>
                                            <td>{timeRange}</td>
                                            <td>{visit.appointmentDate ? formatAppointmentDateDisplay(visit.appointmentDate) : '-'}</td>
                                            <td>{visit.email || '-'}</td>
                                            <td>{visit.phoneNumber ? formatPhoneNumber(visit.phoneNumber) : '-'}</td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className={styles.actionIcons}>
                                                    <span
                                                        className={styles.iconBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRowClick(visit, false); // View
                                                        }}
                                                    >
                                                        <ViewIcon />
                                                    </span>

                                                    <span
                                                        className={styles.iconBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDownload(visit); // Download
                                                        }}
                                                    >
                                                        <DownloadIcons />
                                                    </span>

                                                    {!isDeleted && (<span
                                                        className={styles.iconBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteClick(visit); // Delete
                                                        }}
                                                    >
                                                        <TrashIcon />
                                                    </span>)}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                } else {
                                    // Vendor row
                                    const vendorinfo = row.data as Vendor;
                                    const date = row.date;
                                    const vendorTimeRange = vendorinfo.startTime && vendorinfo.endTime
                                        ? `${moment(vendorinfo.startTime, 'HH:mm A').format('hh:mm A')} - ${moment(vendorinfo.endTime, 'HH:mm A').format('hh:mm A')}`
                                        : '-';

                                    // Use unique key based on vendor ID and date
                                    const uniqueKey = vendorinfo?.id ? `vendor-${vendorinfo.id}-${date?.format('YYYY-MM-DD')}` : `vendor-${index}-${date?.format('YYYY-MM-DD')}`;

                                    return (
                                        <tr
                                            key={uniqueKey}
                                            className={`${styles.tableRow} ${styles.vendorRow}`}
                                            onClick={() => handleRowClick(vendorinfo, true)}
                                        >
                                            <td>-</td>
                                            <td>
                                                <div className={styles.vendorVisitorCell}>
                                                    <MaintenanceIcon size={14} color="#2d8a5f" />
                                                    <span>{vendorinfo.vendorName || '-'}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    className={styles.lockerBadge}
                                                    style={{
                                                        backgroundColor: getLockerColor(vendorinfo.lockerNumber),
                                                    }}
                                                >
                                                    {vendorinfo.lockerNumber}
                                                </span>
                                            </td>
                                            <td>{vendorTimeRange}</td>
                                            <td>{date ? moment(date).format('MM-DD-YYYY') : '-'}</td>
                                            <td>{vendorinfo.vendorEmail || '-'}</td>
                                            <td>{vendorinfo.vendorPhone ? `+${vendorinfo.vendorPhone}` : '-'}</td>
                                            <td onClick={(e) => {
                                                e.stopPropagation();
                                                handleRowClick(vendorinfo, true);
                                            }}>
                                                <div className={styles.actionIcons}>
                                                    <span
                                                        className={styles.iconBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRowClick(vendorinfo, true);
                                                        }}
                                                    >
                                                        <ViewIcon />
                                                    </span>
                                                    <span
                                                        className={styles.iconBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteClick(vendorinfo);
                                                        }}
                                                    >
                                                        <TrashIcon />
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }
                            })
                        ) : (
                            <tr>
                                <td colSpan={8} className={styles.noData}>
                                    No appointments found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {!isSearchDebouncing && filteredRows.length > 0 && (
                <div className={styles.paginationWrapper}>
                    <Pagination
                        currentPage={currentPage}
                        totalCount={filteredRows.length}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </div>
    );
};

export default AppointmentTable;
